import { ExperienceLayouts } from '@/types/gql.generated';
import type { WeekStartDay } from '@/utils/dates';

// Layouts whose week starts on Monday when the view has no explicit weekStartDay.
// This mirrors the original hardcoded behavior (week planners were Monday, month/
// calendar were Sunday), so views with no stored choice render unchanged.
const WEEK_START_MONDAY_LAYOUTS: ReadonlySet<ExperienceLayouts> = new Set([
  ExperienceLayouts.PlannerWeek,
  ExperienceLayouts.PrintableWeek,
]);

/**
 * Per-layout default week start, used when a view's weekStartDay is null
 * ("no explicit choice"): 1 (Monday) for week layouts, 0 (Sunday) otherwise.
 */
export const weekStartDefaultForLayout = (
  layoutId: ExperienceLayouts
): WeekStartDay => (WEEK_START_MONDAY_LAYOUTS.has(layoutId) ? 1 : 0);

/**
 * Resolve the effective week start for a view: an explicit choice (0 = Sunday,
 * 1 = Monday) wins; a null value falls back to the layout default. The column is
 * nullable server-side (`weekStartDay Int?`), so the fallback is live — a view
 * that has never had a week start chosen renders on its layout's default.
 *
 * `weekStartDay` is optional, not just nullable: a selection set that omits it is
 * as valid as one that returns null, and both mean "no explicit choice".
 */
export const resolveWeekStartDay = (experience: {
  weekStartDay?: number | null;
  layoutId: ExperienceLayouts;
}): WeekStartDay =>
  experience.weekStartDay ?? weekStartDefaultForLayout(experience.layoutId);
